import { useEffect, useState, useRef } from "react";
import {
  doc,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import { databasePromise, getAuthAsync } from "configuration";
import { analyticsService } from "services";
import { useLike } from "hooks/useLike";

// Fetch user data by userId
const fetchUserData = async (userId, database) => {
  if (!userId) return null;

  try {
    const userRef = doc(database, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      return {
        firstName: userData.firstName || null,
        lastName: userData.lastName || null,
        email: userData.email || null,
      };
    }
    return null;
  } catch (error) {
    console.error(`Error fetching user data for userId ${userId}:`, error);
    return null;
  }
};

export const useRestaurant = (restaurantId) => {
  const [restaurant, setRestaurant] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const trackingAttempted = useRef(false);
  const userDataRef = useRef(null);       // cached vendor user data
  const fetchedUserIdRef = useRef(null);  // userId we already fetched for

  const { like: likeRestaurant, unlike: unlikeRestaurant } =
    useLike("restaurants");

  // Fetch locations from subcollection
  useEffect(() => {
    if (!restaurantId) {
      setLocations([]);
      setLocationsLoading(false);
      return;
    }

    setLocationsLoading(true);
    let unsubscribe;
    let cancelled;
    databasePromise.then((db) => {
      if (cancelled) return;
      const locationsRef = collection(
        db,
        "restaurants",
        restaurantId,
        "locations",
      );
      unsubscribe = onSnapshot(
        locationsRef,
        (snapshot) => {
          setLocations(
            snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          );
          setLocationsLoading(false);
        },
        (err) => {
          console.error("Error fetching locations:", err);
          setLocations([]);
          setLocationsLoading(false);
        },
      );
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [restaurantId]);

  // Main restaurant data fetching
  useEffect(() => {
    if (!restaurantId) return;

    setLoading(true);
    setError(null);
    trackingAttempted.current = false;
    let unsubscribe;
    let cancelled;

    databasePromise.then((db) => {
      if (cancelled) return;
      const restaurantRef = doc(db, "restaurants", restaurantId);
      unsubscribe = onSnapshot(
        restaurantRef,
        async (snapshot) => {
          try {
            if (snapshot.exists()) {
              const restaurantData = { id: snapshot.id, ...snapshot.data() };

              if (restaurantData.userId) {
                // BUG-F03: only fetch vendor user data once per userId —
                // cache in a ref so subsequent snapshots don't re-trigger
                // a Firestore read on every restaurant document update.
                if (fetchedUserIdRef.current !== restaurantData.userId) {
                  const authInstance = await getAuthAsync();
                  const currentUid = authInstance.currentUser?.uid;

                  if (currentUid) {
                    const userData = await fetchUserData(
                      restaurantData.userId,
                      db,
                    );
                    userDataRef.current = userData;
                    fetchedUserIdRef.current = restaurantData.userId;
                  }
                }

                if (userDataRef.current) {
                  restaurantData.userFirstName = userDataRef.current.firstName;
                  restaurantData.userLastName = userDataRef.current.lastName;
                  restaurantData.userEmail = userDataRef.current.email;
                }
              }

              setRestaurant(restaurantData);
              setLoading(false);

              if (!trackingAttempted.current) {
                trackingAttempted.current = true;
                (async () => {
                  try {
                    const authInstance = await getAuthAsync();
                    const userId = authInstance.currentUser?.uid || null;
                    await analyticsService.trackView("restaurant", restaurantId, userId);
                  } catch (trackError) {
                    console.error("Background tracking failed:", trackError);
                  }
                })();
              }
            } else {
              setRestaurant(null);
              setLoading(false);
            }
          } catch (snapshotError) {
            console.error("Error processing snapshot:", snapshotError);
            setError(snapshotError.message);
            setLoading(false);
          }
        },
        (err) => {
          console.error("Firestore listener error:", err);
          setError(err.message);
          setLoading(false);
        },
      );
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [restaurantId]);

  const getPrimaryLocation = () => {
    // AUD-17: prefer mainLocation from root document (no extra read).
    // Falls back to subcollection for restaurants created before this change.
    if (restaurant?.mainLocation) return restaurant.mainLocation;
    if (!locations || locations.length === 0) return null;
    return locations.find((loc) => loc.isPrimary) || locations[0];
  };

  const getAllLocations = () => locations;

  return {
    restaurant,
    locations,
    loading,
    locationsLoading,
    error,
    likeRestaurant,
    unlikeRestaurant,
    getPrimaryLocation,
    getAllLocations,
  };
};

export const useRestaurantByUserId = (userId) => {
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setRestaurant(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    databasePromise.then(async (db) => {
      try {
        const restaurantsRef = collection(db, "restaurants");
        const q = query(restaurantsRef, where("userId", "==", userId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          const restaurantData = { id: docSnap.id, ...docSnap.data() };

          if (restaurantData.userId) {
            const authInstance = await getAuthAsync();
            const currentUid = authInstance.currentUser?.uid;

            if (currentUid) {
              const userData = await fetchUserData(restaurantData.userId, db);
              if (userData) {
                restaurantData.userFirstName = userData.firstName;
                restaurantData.userLastName = userData.lastName;
                restaurantData.userEmail = userData.email;
              }
            }
          }

          setRestaurant(restaurantData);
        } else {
          setRestaurant(null);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching restaurant by userId:", err);
        setError(err.message);
        setLoading(false);
      }
    });
  }, [userId]);

  return { restaurant, loading, error };
};